import type { ThreadType } from '@kanbu/schema';
import { ThreadCategory } from '@kanbu/schema/enums';
import { formatUtils, DateFormat } from '@kanbu/shared';
import { Trans } from '@lingui/react/macro';
import { cn, Tooltip } from '@utima/ui';
import { PhoneCall } from 'lucide-react';
import { memo } from 'react';

import { OnlineStatus } from './OnlineStatus';

export interface ThreadItemProps {
  onClick: () => void;
  active?: boolean;
  thread: ThreadType;
  isUnread?: boolean;
}

export const ThreadItem = memo(
  ({ onClick, active, thread, isUnread }: ThreadItemProps) => {
    const isVoice = thread.category === ThreadCategory.Voice;
    const callerPhone = isVoice ? thread.chatUser?.phone : undefined;
    const displayTitle = callerPhone || thread.title;

    return (
      <button
        data-cta='admin-inbox-thread-item'
        className={cn(
          'w-full cursor-pointer p-4 text-left hover:bg-primary/15 transition-colors',
          active && 'bg-primary/30',
          isUnread && 'font-bold bg-primary/10',
        )}
        onClick={onClick}
      >
        <div className='flex flex-col-reverse justify-between md:flex-row md:items-center md:gap-2'>
          <span
            className={cn(
              'inline-flex min-w-0 items-center gap-2 text-gray-900 font-medium',
              isUnread && 'font-bold',
            )}
          >
            <OnlineStatus
              showWhenOffline={false}
              withText={false}
              lastActiveAt={thread.lastActiveAt}
            />
            {isVoice && (
              <PhoneCall className='size-3.5 shrink-0 text-green-500' />
            )}
            <Tooltip title={displayTitle || <Trans>Untitled</Trans>}>
              <span className='truncate'>
                {displayTitle || <Trans>Untitled</Trans>}
              </span>
            </Tooltip>
          </span>
          <span className='shrink-0 grow-0 text-xs text-muted-fg'>
            {formatUtils.date(
              thread.lastUserMessageAt ?? thread.createdAt,
              DateFormat.DateTimeNumeric,
            )}
          </span>
        </div>
        <p className='mt-1 hidden truncate text-xs text-gray-600 md:block'>
          {isVoice ? thread.title : thread.chatUser?.id}
        </p>
      </button>
    );
  },
);
